import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SettingsService } from '../settings/settings.service';

interface DocCredito {
  nombre: string;
  descripcion: string;
  subtype: string;
}

interface ArchivoSubido {
  id: number;
  nombre_archivo: string | null;
  updated_at: string | null;
  puede_eliminar: boolean;
  tamano_local?: string;
}

interface DocEstado {
  subtype: string;
  uploaded: boolean;
  archivos: ArchivoSubido[];
}

interface Ejecutivo {
  nombre: string;
  email: string;
  rol: string;
}

type Paso = 'cargando' | 'invalido' | 'expirado' | 'listo';

const PASOS_PROCESANDO = ['Archivo recibido', 'Validando formato', 'Analizando contenido', 'Registrando información'];

@Component({
  selector: 'app-documentacion-credito',
  standalone: false,
  templateUrl: './documentacion-credito.component.html',
  styleUrl: './documentacion-credito.component.less',
})
export class DocumentacionCreditoComponent implements OnInit {

  readonly logoUrl = 'https://simpliassets12daacw.s3.us-east-2.amazonaws.com/icon.png';
  readonly pasosProcesando = PASOS_PROCESANDO;

  // Mismo listado/orden que DOCS_CREDITO en ceo-creditos.component.ts /
  // simulacion-credito.component.ts (excluye solo 4.6, deshabilitado ahí también).
  readonly DOCS_CREDITO: DocCredito[] = [
    { nombre: 'Constitución de Sociedad', descripcion: 'Documento legal de la empresa', subtype: '4.1' },
    { nombre: 'Balance 2 años', descripcion: 'Estados financieros de los últimos 2 años', subtype: '4.2' },
    { nombre: 'Cuadro de Deudas', descripcion: 'Detalle de obligaciones financieras vigentes', subtype: '4.3' },
    { nombre: 'Cuadro de Contratos por Facturar', descripcion: 'Contratos pendientes de facturación', subtype: '4.4' },
    { nombre: 'Contrato u Orden de Compra', descripcion: 'Documento que justifica el flujo', subtype: '4.5' },
    { nombre: 'Estado de Situación de Cliente y Socios', descripcion: 'Estado patrimonial completo del cliente', subtype: '4.7' },
    { nombre: 'Certificado CBR', descripcion: 'Conservador de Bienes Raíces', subtype: '4.8' },
    { nombre: 'Informe Legal', descripcion: 'Informe legal de la operación', subtype: '4.9' },
    { nombre: 'CI del Avalista', descripcion: 'CI de cada avalista, ambos lados', subtype: '4.10' },
    { nombre: 'Cédula de Identidad', descripcion: 'Ambos lados del documento', subtype: '1.6' },
  ];

  private codigo = '';
  paso: Paso = 'cargando';

  empresaNombre = '';
  operacionId: number | null = null;
  simulacionId: number | null = null;
  ejecutivo: Ejecutivo | null = null;
  estado = '';
  documentos: { [subtype: string]: DocEstado } = {};
  subtypeSubiendo: string | null = null;
  docExpandido: { [subtype: string]: boolean } = {};
  verDocumentoLoadingIds = new Set<number>();
  eliminarLoadingIds = new Set<number>();

  // ── Modal "Procesando documento..." ──────────────────────────────────────
  mostrarModalProcesando = false;
  procesandoNombreArchivo = '';
  pasoProcesandoActual = 0; // pasos con index < esto están "done"; el index === esto está "activo"
  procesandoError = false;

  constructor(
    private route: ActivatedRoute,
    private snackbar: MatSnackBar,
    public settings: SettingsService,
  ) {}

  ngOnInit(): void {
    this.codigo = this.route.snapshot.paramMap.get('token') || '';
    this.cargarDetalle();
  }

  get completados(): number {
    return this.DOCS_CREDITO.filter(d => this.documentos[d.subtype]?.uploaded).length;
  }

  get pendientes(): number {
    return this.DOCS_CREDITO.length - this.completados;
  }

  get progresoPct(): number {
    return Math.round((this.completados / this.DOCS_CREDITO.length) * 100);
  }

  get ejecutivoIniciales(): string {
    const partes = (this.ejecutivo?.nombre ?? '').trim().split(/\s+/);
    const primera = partes[0]?.[0] ?? '';
    const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
    return (primera + ultima).toUpperCase();
  }

  fechaFormateada(iso: string | null): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  toggleExpandido(subtype: string): void {
    this.docExpandido[subtype] = !this.docExpandido[subtype];
  }

  private formatearTamano(bytes: number): string {
    const kb = bytes / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(1)} KB`;
  }

  private async cargarDetalle(): Promise<void> {
    if (!this.codigo) {
      this.paso = 'invalido';
      return;
    }
    try {
      const res: any = await this.settings.get_query(1, `creditos/documentacion-publica/${this.codigo}/`, {}, true);
      if (res?.status !== 200 || !res?._body) {
        this.paso = 'invalido';
        return;
      }
      const body = res._body;
      this.empresaNombre = body.company?.business_name ?? '';
      this.operacionId = body.operacion_id ?? null;
      this.simulacionId = body.simulacion_id ?? null;
      this.ejecutivo = body.ejecutivo ?? null;
      this.estado = body.estado ?? '';
      const mapa: { [subtype: string]: DocEstado } = {};
      (body.documentos ?? []).forEach((d: any) => {
        // conserva el tamaño calculado en esta sesión para archivos ya conocidos
        // (el backend no guarda el tamaño en bytes de lo ya subido)
        const anteriores = this.documentos[d.subtype]?.archivos ?? [];
        const archivos: ArchivoSubido[] = (d.archivos ?? []).map((a: ArchivoSubido) => ({
          ...a,
          tamano_local: anteriores.find(prev => prev.id === a.id)?.tamano_local,
        }));
        mapa[d.subtype] = { subtype: d.subtype, uploaded: d.uploaded, archivos };
      });
      this.documentos = mapa;
      this.paso = 'listo';
    } catch (err: any) {
      this.paso = err?.statusCode === 410 ? 'expirado' : 'invalido';
    }
  }

  triggerUpload(subtype: string): void {
    if (this.subtypeSubiendo) return;
    const input = document.getElementById(`doc-upload-${subtype}`) as HTMLInputElement;
    if (input) { input.value = ''; input.click(); }
  }

  async onFileSelected(event: any, subtype: string): Promise<void> {
    const file: File | undefined = event.target.files?.[0];
    if (!file) return;

    this.subtypeSubiendo = subtype;
    this.mostrarModalProcesando = true;
    this.procesandoNombreArchivo = file.name;
    this.procesandoError = false;
    this.pasoProcesandoActual = 0; // "Archivo recibido" activo de inmediato

    const formData = new FormData();
    formData.append('subtype', subtype);
    formData.append('file', file);

    try {
      const res: any = await this.settings.get_query(
        2,
        `creditos/documentacion-publica/${this.codigo}/subir/`,
        formData,
        true,
      );
      if (res?.status !== 200) {
        throw new Error('upload_failed');
      }

      // El OCR corre en segundo plano en el backend — al cliente no le interesa
      // ni debe esperarlo. La animación de pasos es puramente cosmética (tiempos
      // fijos), no está atada a ningún estado real de procesamiento.
      await this.avanzarPasosFicticios();

      const tamano = this.formatearTamano(file.size);
      await this.cargarDetalle();
      // El archivo recién subido queda primero (el backend ordena por -id).
      const nuevo = this.documentos[subtype]?.archivos?.[0];
      if (nuevo) { nuevo.tamano_local = tamano; }

      this.mostrarModalProcesando = false;
      this.snackbar.open('Documento subido correctamente', 'Cerrar', { duration: 2500, panelClass: 'simpli-snack' });
    } catch (err: any) {
      if (err?.statusCode === 410) {
        this.mostrarModalProcesando = false;
        this.paso = 'expirado';
      } else {
        this.procesandoError = true;
        const mensaje = err?.error?.error ?? 'No pudimos procesar este archivo';
        this.snackbar.open(mensaje, 'Cerrar', { duration: 3500, panelClass: 'simpli-snack' });
      }
    } finally {
      this.subtypeSubiendo = null;
    }
  }

  cerrarModalProcesando(): void {
    this.mostrarModalProcesando = false;
  }

  /** Animación cosmética de tiempo fijo (~900ms por paso) — no consulta nada real. */
  private avanzarPasosFicticios(): Promise<void> {
    return new Promise(resolve => {
      const total = this.pasosProcesando.length;
      const tick = () => {
        this.pasoProcesandoActual++;
        if (this.pasoProcesandoActual < total) {
          setTimeout(tick, 900);
        } else {
          setTimeout(resolve, 600);
        }
      };
      setTimeout(tick, 900);
    });
  }

  async verDocumento(archivo: ArchivoSubido | undefined): Promise<void> {
    if (!archivo?.id || this.verDocumentoLoadingIds.has(archivo.id)) return;
    this.verDocumentoLoadingIds.add(archivo.id);
    try {
      const res: any = await this.settings.get_query(
        1,
        `creditos/documentacion-publica/${this.codigo}/documentos/${archivo.id}/ver/`,
        {},
        true,
      );
      const url: string = res?._body?.url;
      if (url) { window.open(url, '_blank'); }
    } catch {
      this.snackbar.open('No se pudo abrir el documento', 'Cerrar', { duration: 3000, panelClass: 'simpli-snack' });
    } finally {
      this.verDocumentoLoadingIds.delete(archivo.id);
    }
  }

  archivoAEliminar: ArchivoSubido | null = null;

  eliminarDocumento(subtype: string, archivo: ArchivoSubido | undefined): void {
    if (!archivo?.id || !archivo.puede_eliminar || this.eliminarLoadingIds.has(archivo.id)) return;
    this.archivoAEliminar = archivo;
  }

  cancelarEliminar(): void {
    this.archivoAEliminar = null;
  }

  async confirmarEliminar(): Promise<void> {
    const archivo = this.archivoAEliminar;
    if (!archivo) return;
    this.archivoAEliminar = null;

    this.eliminarLoadingIds.add(archivo.id);
    try {
      const res: any = await this.settings.get_query(
        2,
        `creditos/documentacion-publica/${this.codigo}/documentos/${archivo.id}/eliminar/`,
        {},
        true,
      );
      if (res?.status !== 200) {
        throw new Error('delete_failed');
      }
      await this.cargarDetalle();
      this.snackbar.open('Documento eliminado', 'Cerrar', { duration: 2500, panelClass: 'simpli-snack' });
    } catch (err: any) {
      if (err?.statusCode === 410) {
        this.paso = 'expirado';
      } else {
        this.snackbar.open('No se pudo eliminar el documento', 'Cerrar', { duration: 3000, panelClass: 'simpli-snack' });
      }
    } finally {
      this.eliminarLoadingIds.delete(archivo.id);
    }
  }
}
